import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { RegisterAuth } from '@carvana/core-auth';
import CarvanaHeader, { getHeaderManifest } from '@carvana/header';
import CarvanaFooter from '@carvana/footer';
import { type Configuration as MfeConfiguration, MfeConfig } from '@carvana/microfrontend';
import { MfeReact, MfeReactComponent } from '@carvana/microfrontend-react';
import { environment, isLocalEnvironment } from './environment';
import type { RootState } from './store/types';
import { sharedSelectors, sharedOperations } from './shared/ducks';
import { personalDetailsOperations } from './tabs/PersonalDetails/ducks';
import MobileNavBar from './shared/components/NavBar/MobileNavBar';
import Breadcrumb from './shared/components/Breadcrumb';
import DesktopNavBar from './shared/components/NavBar/DesktopNavBar';
import RouterOutlet from './routes';
import { financingTermsOperations } from './tabs/FinancingTerms/ducks';
import styles from './Account.module.css';
import '@carvana/footer/styles.css';
import '@carvana/header/styles.css';

const Main = styled.main`
  width: 100%;
  min-height: 700px;
  background-color: ${(props: any) => props.theme.carvana.gray.background};

  @media (min-width: ${(props: any) => props.theme.breakpoints.values.sm}px) {
    display: flex;
    min-height: 100vh;
    max-width: 1800px;
    margin: 0 auto;
  }
`;

function mfeCacheBuster() {
  const date = new Date();
  const nearestQuarterHour = Math.round(date.getMinutes() / 15) * 15;

  return `${date.getDate()}${date.getHours()}${nearestQuarterHour}`;
}

RegisterAuth({
  environment,
  name: 'account-ui',
  isLocalInstance: isLocalEnvironment
});

const config: MfeConfiguration = {
  name: 'accountUi',
  debug: false,
  Render: MfeReact(),
  ENV: environment,
  globals: {
    React: () => import('react'),
    ReactDOM: () => import('react-dom'),
    styled: () => import('styled-components'),
    'styled-components': () => import('styled-components'),
    PropTypes: () => import('prop-types'),
    'prop-types': () => import('prop-types')
  },
  files: [
    [
      'FooterModule',
      [
        {
          component: 'default',
          browser: true,
          default: {
            file: 'index.js',
            manifest: `${
              process.env.CARVANA_APP_CDN_URL_FASTLY
            }/footer/module/assets-manifest.json?v=${mfeCacheBuster()}`
          }
        }
      ]
    ],
    [
      'HeaderModule',
      [
        {
          component: 'default',
          default: {
            file: 'index.js',
            manifest: getHeaderManifest(environment)
          }
        }
      ]
    ]
  ]
};

type StateProps = {
  isMobile: boolean;
  shouldRenderMobileNavigation: boolean;
};

type DispatchProps = {
  fetchAppSettings: () => void;
  fetchFinancingTerms: () => void;
  fetchPersonalDetails: () => void;
  fetchPurchases: () => void;
};

function Account({
  fetchPersonalDetails,
  fetchFinancingTerms,
  fetchAppSettings,
  fetchPurchases,
  isMobile,
  shouldRenderMobileNavigation
}: StateProps & DispatchProps) {
  useEffect(() => {
    MfeConfig(config);
    fetchPersonalDetails();
    fetchFinancingTerms();
    fetchAppSettings();
    fetchPurchases();
  }, []);

  const shouldShowMobileNavBar = isMobile && shouldRenderMobileNavigation;
  const shouldShowMobileBreadcrumb = isMobile && !shouldRenderMobileNavigation;

  return (
    <>
      <MfeReactComponent
        component="default"
        module="HeaderModule"
        environment={environment}
        loading={<CarvanaHeader />}
      />
      <Main>
        {shouldShowMobileNavBar && <MobileNavBar />}
        {shouldShowMobileBreadcrumb && <Breadcrumb />}
        {!isMobile && <DesktopNavBar />}
        <RouterOutlet />
      </Main>

      <MfeReactComponent
        component="default"
        module="FooterModule"
        withoutProvider
        loading={
          <CarvanaFooter
            environment={environment}
            withoutProvider
            wrapperClassName={styles.footer}
          />
        }
        wrapperClassName={styles.footer}
      />
    </>
  );
}

const mapDispatchToProps = {
  fetchPersonalDetails: personalDetailsOperations.fetchPersonalDetails,
  fetchFinancingTerms: financingTermsOperations.fetchFinancingTerms,
  fetchAppSettings: sharedOperations.fetchAppSettings,
  fetchPurchases: personalDetailsOperations.fetchPurchases
};

const mapStateToProps = (state: RootState) => ({
  isMobile: sharedSelectors.getIsMobile(state),
  shouldRenderMobileNavigation: sharedSelectors.getShouldRenderMobileNavigation(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(Account);
